import { useEffect, useState } from "react";
import { NavLink, Link, useLocation } from "react-router-dom";
import { Menu, X, Wrench } from "lucide-react";
import { SITE } from "../lib/site";

const LINKS = [
  { to: "/", label: "Home" },
  { to: "/brands", label: "Brands" },
  { to: "/services", label: "Services" },
  { to: "/motorcycles", label: "Moto" },
  { to: "/book", label: "Book" },
  { to: "/build", label: "Build" },
  { to: "/about", label: "About" },
  { to: "/gallery", label: "Gallery" },
  { to: "/contact", label: "Contact" },
];

export const Navbar = () => {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const loc = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => { setOpen(false); }, [loc.pathname]);

  return (
    <header
      data-testid="site-navbar"
      className={`fixed top-8 inset-x-0 z-50 transition-colors duration-300 ${
        scrolled ? "backdrop-blur-xl bg-black/70 border-b border-white/10" : "bg-transparent"
      }`}
    >
      <div className="px-6 md:px-12 lg:px-24 h-16 md:h-20 flex items-center justify-between">
        <Link to="/" data-testid="nav-logo-link" className="flex items-center gap-3 group">
          <img
            src="/logo.jpg"
            alt="Dirty Wrenches"
            className="w-11 h-11 md:w-12 md:h-12 rounded-full object-cover ring-1 ring-white/15"
          />
          <div className="leading-none">
            <div className="font-display font-black uppercase text-white tracking-tight text-lg md:text-xl">
              {SITE.name}
            </div>
            <div className="font-mono-tech text-[10px] text-white/40 tracking-[0.25em] uppercase">
              Automotive · {SITE.est}
            </div>
          </div>
        </Link>

        <nav className="hidden md:flex items-center gap-1">
          {LINKS.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              end={l.to === "/"}
              data-testid={`nav-link-${l.label.toLowerCase()}`}
              className={({ isActive }) =>
                `px-4 py-2 text-sm font-medium uppercase tracking-wider transition-colors ${
                  isActive ? "text-white" : "text-white/55 hover:text-white"
                }`
              }
            >
              {({ isActive }) => (
                <span className="relative">
                  {l.label}
                  {isActive && (
                    <span className="absolute left-0 -bottom-1 h-0.5 w-full bg-[#FF3B30]" />
                  )}
                </span>
              )}
            </NavLink>
          ))}
        </nav>

        <div className="hidden md:block">
          <Link
            to="/book"
            data-testid="nav-book-btn"
            className="group inline-flex items-center gap-2 bg-[#FF3B30] hover:bg-[#FF5722] text-white text-xs font-bold uppercase tracking-[0.2em] px-5 py-3 transition-colors"
          >
            Book appt
            <span className="transition-transform group-hover:translate-x-1">→</span>
          </Link>
        </div>

        <button
          data-testid="nav-mobile-toggle"
          onClick={() => setOpen((v) => !v)}
          className="md:hidden text-white p-2"
          aria-label="Toggle menu"
        >
          {open ? <X /> : <Menu />}
        </button>
      </div>

      {open && (
        <div data-testid="nav-mobile-menu" className="md:hidden border-t border-white/10 bg-black/95 backdrop-blur-xl">
          <div className="px-6 py-4 flex flex-col">
            {LINKS.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                end={l.to === "/"}
                data-testid={`nav-mobile-link-${l.label.toLowerCase()}`}
                className={({ isActive }) =>
                  `py-3 text-base uppercase tracking-wider border-b border-white/5 ${
                    isActive ? "text-[#FF3B30]" : "text-white/70"
                  }`
                }
              >
                {l.label}
              </NavLink>
            ))}
            <Link
              to="/book"
              data-testid="nav-mobile-book-btn"
              className="mt-4 inline-flex justify-center items-center gap-2 bg-[#FF3B30] text-white text-xs font-bold uppercase tracking-[0.2em] px-5 py-3"
            >
              Book appt →
            </Link>
          </div>
        </div>
      )}
    </header>
  );
};

export default Navbar;
